/** Precisão para validação da soma dos fatores (6 casas decimais). */
const SUM_MIN = 0.999999;
const SUM_MAX = 1.000001;

export type WeightingFactorsResult =
  | { valid: true; factors: number[] }
  | { valid: false; message: string };

/**
 * Valida apenas formato e soma dos fatores (para feedback em tempo real).
 * Não verifica quantidade vs num_cenarios. Use quando o número de cenários ainda não é conhecido.
 */
const MSG_CONDICAO2_FLOAT = 'Todos os fatores devem ser float, não negativos, entre 0 e 1.';
const MSG_CONDICAO3 = 'Soma dos fatores deve ser igual a 1 (precisão de 6 casas decimais: 0,999999 a 1,000001).';

export function validateWeightingFactorsFormat(text: string): WeightingFactorsResult {
  const trimmed = text.trim();
  if (trimmed === '') {
    return {
      valid: false,
      message: 'Informe os fatores de ponderação separados por vírgula.',
    };
  }
  const parts = trimmed.split(',').map((s) => s.trim()).filter(Boolean);
  const factors: number[] = [];
  for (let i = 0; i < parts.length; i += 1) {
    const n = Number.parseFloat(parts[i]);
    if (Number.isNaN(n)) {
      return {
        valid: false,
        message: `${MSG_CONDICAO2_FLOAT} Valor inválido na posição ${i + 1}: informe um número.`,
      };
    }
    if (n < 0 || n > 1) {
      return {
        valid: false,
        message: `${MSG_CONDICAO2_FLOAT} Valor na posição ${i + 1} (${n}) está fora do intervalo.`,
      };
    }
    factors.push(n);
  }
  const sum = factors.reduce((a, b) => a + b, 0);
  if (sum < SUM_MIN || sum > SUM_MAX) {
    return {
      valid: false,
      message: `${MSG_CONDICAO3} Soma atual: ${sum.toFixed(6)}.`,
    };
  }
  return { valid: true, factors };
}

/**
 * Valida fatores de ponderação para cenários probabilísticos.
 * Regras: num_fatores === num_cenarios; cada fator float em [0, 1]; soma em [0,999999, 1,000001].
 */
const MSG_CONDICAO1 = 'O número de fatores deve ser igual ao número de cenários.';

export function validateWeightingFactors(
  text: string,
  numCenarios: number,
): WeightingFactorsResult {
  const trimmed = text.trim();
  if (trimmed === '') {
    return {
      valid: false,
      message: 'Informe os fatores de ponderação separados por vírgula.',
    };
  }
  const parts = trimmed.split(',').map((s) => s.trim()).filter(Boolean);
  if (parts.length !== numCenarios) {
    return {
      valid: false,
      message: `${MSG_CONDICAO1} Fatores informados: ${parts.length}. Cenários: ${numCenarios}.`,
    };
  }
  const factors: number[] = [];
  for (let i = 0; i < parts.length; i += 1) {
    const n = Number.parseFloat(parts[i]);
    if (Number.isNaN(n)) {
      return {
        valid: false,
        message: `${MSG_CONDICAO2_FLOAT} Valor inválido na posição ${i + 1}: informe um número.`,
      };
    }
    if (n < 0 || n > 1) {
      return {
        valid: false,
        message: `${MSG_CONDICAO2_FLOAT} Valor na posição ${i + 1} (${n}) está fora do intervalo.`,
      };
    }
    factors.push(n);
  }
  const sum = factors.reduce((a, b) => a + b, 0);
  if (sum < SUM_MIN || sum > SUM_MAX) {
    return {
      valid: false,
      message: `${MSG_CONDICAO3} Soma atual: ${sum.toFixed(6)}.`,
    };
  }
  return { valid: true, factors };
}
